// src/pages/NewsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import NewsletterSignup from '../components/News/NewsletterSignup';
import { useNewsPosts, parseYMD, formatDate, monthLabel } from '../hooks/useNewPosts';

export default function NewsPage() {
    const { articles, loading, error } = useNewsPosts(); // all posts

    const yearToMonths = useMemo(() => {
        const map = new Map();
        for (const a of articles) {
            const d = parseYMD(a.date);
            const y = d.getFullYear();
            const m = d.getMonth() + 1;
            if (!map.has(y)) map.set(y, new Set());
            map.get(y).add(m);
        }
        return map;
    }, [articles]);

    const yearOptions = useMemo(
        () => Array.from(yearToMonths.keys()).sort((a, b) => b - a),
        [yearToMonths]
    );

    const [year, setYear] = useState('all');
    const [month, setMonth] = useState(0);

    const monthSetForUI = useMemo(() => {
        if (year === 'all') {
            const union = new Set();
            for (const s of yearToMonths.values()) for (const m of s) union.add(m);
            return union;
        }
        return yearToMonths.get(Number(year)) ?? new Set();
    }, [year, yearToMonths]);

    const monthOptions = useMemo(
        () => Array.from(monthSetForUI).sort((a, b) => b - a),
        [monthSetForUI]
    );

    useEffect(() => {
        if (month !== 0 && !monthSetForUI.has(month)) setMonth(0);
    }, [month, monthSetForUI]);

    const filtered = useMemo(
        () =>
            articles.filter((a) => {
                const d = parseYMD(a.date);
                const yOk = year === 'all' ? true : d.getFullYear() === Number(year);
                const mOk = month === 0 ? true : d.getMonth() + 1 === Number(month);
                return yOk && mOk;
            }),
        [articles, year, month]
    );

    // Popup state
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null);
    const [imgIndex, setImgIndex] = useState(0);

    const openArticle = (article) => {
        setActive(article);
        setOpen(true);
        setImgIndex(0);
    };

    const close = () => {
        setOpen(false);
        setActive(null);
        setImgIndex(0);
    };

    let content;
    if (loading) {
        content = (
            <Typography variant="body2" color="text.secondary">
                Loading news…
            </Typography>
        );
    } else if (error) {
        content = (
            <Typography variant="body2" color="error">
                {error}
            </Typography>
        );
    } else if (!filtered.length) {
        content = (
            <Typography variant="body2" color="text.secondary">
                No news posts match your filters yet. Please try a different year or
                month, or check back soon.
            </Typography>
        );
    } else {
        content = filtered.map((a) => (
            <Box
                key={a.id}
                onClick={() => openArticle(a)}
                sx={(theme) => ({
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                    '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-1px)',
                    },
                    backgroundColor: theme.palette.background.paper,
                })}
            >
                {a.coverUrl && (
                    <Box
                        component="img"
                        src={a.coverUrl}
                        alt={a.title}
                        sx={{
                            width: { xs: '100%', sm: 220 },
                            height: { xs: 180, sm: 150 },
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                )}

                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                    >
                        {formatDate(a.date)}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {a.title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            flexGrow: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {a.summary || a.body}
                    </Typography>
                    <Button
                        size="small"
                        sx={{ alignSelf: 'flex-start', mt: 1, fontWeight: 600 }}
                    >
                        View story →
                    </Button>
                </Box>
            </Box>
        ));
    }

    const activeText = (active?.body || active?.summary || '').split(/\n{2,}/);
    const activeParagraphs = activeText
        .map((s) => s.trim())
        .filter(Boolean);

    return (
        <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
            <Container
                maxWidth="lg"
                sx={{
                    py: { xs: 4, md: 5 },
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    gap: { xs: 3, md: 4 },
                }}
            >
                {/* Newsletter stays at the top */}
                <Box>
                    <NewsletterSignup />
                </Box>

                {/* Header + Filters */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.8rem', md: '2rem' },
                        }}
                    >
                        News &amp; Updates
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="news-year-page-label">Year</InputLabel>
                            <Select
                                labelId="news-year-page-label"
                                label="Year"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                            >
                                <MenuItem value="all">All years</MenuItem>
                                {yearOptions.map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="news-month-page-label">Month</InputLabel>
                            <Select
                                labelId="news-month-page-label"
                                label="Month"
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                            >
                                <MenuItem value={0}>All months</MenuItem>
                                {monthOptions.map((m) => (
                                    <MenuItem key={m} value={m}>
                                        {monthLabel(m)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>

                {/* Scrollable news list box */}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <Card
                        elevation={2}
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            height: { xs: '60vh', md: '65vh' },
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                p: { xs: 2, md: 3 },
                            }}
                        >
                            {content}
                        </Box>
                    </Card>
                </Box>
            </Container>

            {/* Popup with slideshow + full story */}
            <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pr: 6 }}>
                    {active?.title}
                    <IconButton
                        onClick={close}
                        aria-label="Close"
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            bgcolor: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ position: 'relative' }}>
                    {active && (
                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    maxHeight: { xs: '52vh', md: '60vh' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    bgcolor: 'common.black',
                                }}
                            >
                                {Array.isArray(active.images) &&
                                    active.images.length > 1 && (
                                        <>
                                            <IconButton
                                                aria-label="Previous photo"
                                                onClick={() =>
                                                    setImgIndex(
                                                        (i) =>
                                                            (i -
                                                                1 +
                                                                active.images
                                                                    .length) %
                                                            active.images.length
                                                    )
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    left: 8,
                                                    top: '50%',
                                                    transform:
                                                        'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor:
                                                            'rgba(0,0,0,0.7)',
                                                    },
                                                }}
                                            >
                                                <ChevronLeftIcon />
                                            </IconButton>
                                            <IconButton
                                                aria-label="Next photo"
                                                onClick={() =>
                                                    setImgIndex(
                                                        (i) =>
                                                            (i + 1) %
                                                            active.images.length
                                                    )
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform:
                                                        'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor:
                                                            'rgba(0,0,0,0.7)',
                                                    },
                                                }}
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </>
                                    )}

                                {active.images && active.images.length > 0 && (
                                    <Box
                                        component="img"
                                        src={active.images[imgIndex]}
                                        alt={active.title}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                )}
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {formatDate(active.date)}
                                </Typography>
                                {activeParagraphs.map((p, i) => (
                                    <Typography
                                        key={i}
                                        variant="body2"
                                        sx={{ mt: i === 0 ? 1 : 1.25 }}
                                    >
                                        {p}
                                    </Typography>
                                ))}
                                {Array.isArray(active.images) &&
                                    active.images.length > 1 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 1 }}
                                        >
                                            Photo {imgIndex + 1} of{' '}
                                            {active.images.length}
                                        </Typography>
                                    )}
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
